import { useEffect, useRef, useState } from "react";
import { useTargeting } from "./TargetingContext";

const FOCUS_MS = 2000;

// Scroll an area card into view and flash a focus ring when the map's "View"
// confirmation targets it (AC23). The highlight target carries a nonce so
// repeat highlights of the same id re-fire. The un-focus timer lives in a ref
// (not the effect cleanup) so highlighting another card — which re-runs the
// effect — doesn't cancel this card's pending fade-out, letting each highlighted
// card fade independently. Shared by the D2D AreaCard and the H2H area card.
export const useAreaCardFocus = (areaId: string) => {
  const { highlightTarget } = useTargeting();
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [focused, setFocused] = useState(false);
  // The highlight signal now outlives the step (the provider sits above the
  // shell, PAD-31560). A card remounting on return to Targeting must not replay a
  // highlight set during an earlier visit, so we ignore whatever nonce was already
  // present at mount and only flash on a genuinely new one.
  const seenNonce = useRef(highlightTarget?.nonce ?? null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (highlightTarget?.id !== areaId || highlightTarget.nonce === seenNonce.current) return;
    seenNonce.current = highlightTarget.nonce;
    ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setFocused(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFocused(false), FOCUS_MS);
  }, [highlightTarget, areaId]);

  return { ref, focused };
};
